import { useDiscoverStore } from "@/stores/useDiscoverStore";

/**
 * Session flag that routes a sign-up through the volunteer worker onboarding:
 * OnboardingPage shows the volunteer variant, GetReady continues to Discover,
 * and Discover opens on the $0 preset (consuming the flag).
 *
 * Lives here, not in VolunteerLandingPage, so the app entry can set it without
 * pulling a lazy route into the main bundle.
 */
export const VOLUNTEER_WORKER_KEY = "wexare_volunteer_worker";

/**
 * Printed campaign material (posters with a QR) links to any page — e.g.
 * /bounties — with `utm_content=volunteers`. The URL is already out in the
 * world, so the app has to honour it rather than the link being changed.
 */
export function isVolunteerCampaign(search: string): boolean {
  const content = new URLSearchParams(search).get("utm_content");
  if (!content) return false;
  const v = content.trim().toLowerCase();
  return v === "volunteers" || v === "volunteer";
}

/**
 * Run once at app boot, before any redirect can drop the query string: the
 * flag must survive the Privy login and AuthGuard's bounce to /onboarding,
 * which is why it goes into sessionStorage (same tab) and not component state.
 */
export function captureVolunteerCampaign(search: string): boolean {
  if (!isVolunteerCampaign(search)) return false;
  try {
    sessionStorage.setItem(VOLUNTEER_WORKER_KEY, "1");
  } catch {
    /* storage unavailable — the $0 preset below still applies */
  }
  useDiscoverStore.getState().setBudgetPreset("volunteer");
  return true;
}

/** Whether this tab carries the volunteer tag (campaign link or /volunteer). */
export function isVolunteerSession(): boolean {
  try {
    return !!sessionStorage.getItem(VOLUNTEER_WORKER_KEY);
  } catch {
    return false;
  }
}

export interface VolunteerHandoffInput {
  isAuthenticated: boolean;
  profileSynced: boolean;
  isOnboarded: boolean;
  isVolunteer: boolean;
  /** "I'm interested" in flight — that door onboards in one tap by itself. */
  hasFrictionlessContext: boolean;
  pathname: string;
  search: string;
}

/**
 * Where a tagged volunteer goes the moment they log in on a public page
 * (/bounties, /bounty/:id). Those pages sit outside AuthGuard, so without
 * this a sign-up from the navbar just stays put and never sees the
 * volunteer onboarding. Onboarded members are left where they are.
 *
 * `returnUrl` brings someone who signed in on a quest back to it after
 * GetReady; from the list, GetReady's volunteer fallback (Discover) is right.
 */
export function volunteerHandoff(
  s: VolunteerHandoffInput,
): { to: string; returnUrl: string | null } | null {
  if (!s.isAuthenticated || !s.profileSynced) return null;
  if (s.isOnboarded || !s.isVolunteer || s.hasFrictionlessContext) return null;
  return {
    to: "/onboarding",
    returnUrl: s.pathname.startsWith("/bounty/") ? `${s.pathname}${s.search}` : null,
  };
}
